<template>
    <b-row>
        <b-col cols="12">
            <!-- ************************************************************************************** -->
            <!-- *                             UPPER PAGINATION CONTROLS                              * -->
            <!-- ************************************************************************************** -->
            <table-pagination-controls
                :pollsTableTotalPages="pollsTableTotalPages"
                :numberOfPolls="numberOfPolls"
            />

            <!-- Filter by column selection -->
            <b-row class="mx-0 ms-3">
                <b-col class="ps-0">
                    <b>Filter by: </b>
                    <b-form-checkbox-group v-model="FilterTableFields" class="">
                        <b-form-checkbox value="District">{{ getLabel("District") }}</b-form-checkbox>
                        <b-form-checkbox value="Date">{{ getLabel("Date") }}</b-form-checkbox>
                        <b-form-checkbox value="Pollster">{{ getLabel("Pollster") }}</b-form-checkbox>
                        <b-form-checkbox value="Link">{{ getLabel("Link") }}</b-form-checkbox>
                        <b-form-checkbox value="Notes">{{ getLabel("Notes") }}</b-form-checkbox>
                        <b-form-checkbox value="Source">{{ getLabel("Source") }}</b-form-checkbox>
                    </b-form-checkbox-group>
                </b-col>
                <b-col v-if="enableFuturePolls === true" class="ps-0">
                    <data-table-checkbox-filter
                        title="Show Polls"
                        v-model="filterFuturePolls"
                        :initOptions ="['Current', 'Future']"
                        :initSelected ="filterFuturePolls"
                    >
                    </data-table-checkbox-filter>
                    <slot name="filterColRight"></slot>
                </b-col>
            </b-row>

            <!-- ************************************************************************************** -->
            <!-- *                                  POLLS DATA TABLE                                  * -->
            <!-- ************************************************************************************** -->
            <b-row>
                <b-col>
                    <b-table id="polls-table"
                        hover
                        :provider="pollsFilter"
                        :fields="pollDataTableFields"
                        :details-provider="isRowExpanded"
                        :per-page="pollsPerPage"
                        :current-page="pollsTableCurrentPage"
                        :filter="PollsTableFilter"
                        :filterIncludedFields="FilterTableFields"
                        v-model:sort-by="sortBy"
                        v-model:sort-direction="sortDirection"
                        :multisort="false"
                        :tbody-tr-class="rowClass"
                    >
                        <template #head(D_Topline)="scope">
                            <slot name="head-D_Topline"><i class='fas fa-democrat'></i></slot>
                        </template>
                        <template #head(R_Topline)="scope">
                            <slot name="head-R_Topline"><i class='fas fa-republican'></i></slot>
                        </template>
                        <template #head(Generic_D)="scope">
                            <slot name="head-Generic_D"><i class='fas fa-democrat'></i></slot>
                        </template>
                        <template #head(Generic_R)="scope">
                            <slot name="head-Generic_R"><i class='fas fa-republican'></i></slot>
                        </template>
                        <template #head(President_Approval)="scope">
                            <slot name="head-President_Approval)">
                                <!-- Use the short name (eg. Presidential election cycle) -->
                                <span v-if="configHasPresCandDShortName">
                                    {{configGetPresCandDShortName}}
                                </span>
                                <!-- Use thumbs up (eg. Non - Presidential election cycle) -->
                                <span v-else>
                                    <i class="fas fa-thumbs-up"></i>
                                </span>
                            </slot>
                        </template>
                        <template #head(President_Disapproval)="scope">
                            <slot name="head(President_Disapproval)">
                                <!-- Use the short name (eg. Presidential election cycle) -->
                                <span v-if="configHasPresCandRShortName">
                                    {{configGetPresCandRShortName}}
                                </span>
                                <!-- Use thumbs down (eg. Non - Presidential election cycle) -->
                                <span v-else>
                                    <i class="fas fa-thumbs-down"></i>
                                </span>
                            </slot>
                        </template>

                        <template v-slot:thead-top="data">
                            <b-tr>
                                <!-- <b-th colspan="3" style="border-top:0;"><span class="sr-only">Poll Info</span></b-th> -->
                                <b-th v-if="displayGenericCandidate === true" colspan="2" style="border-top:0; width:25%">
                                    <b-input-group>
                                        <b-form-input 
                                            id="polls-table-filter-input"
                                            v-model="PollsTableTextFilter"
                                            type="search"
                                            :placeholder="filterInputPlaceholderText"
                                            @keypress="updateDataTablePage"
                                            debounce="500"
                                        ></b-form-input>
                                        <template #append>
                                            <b-button :disabled="!PollsTableTextFilter" @click="PollsTableTextFilter = ''">Clear Filter</b-button>
                                        </template>
                                    </b-input-group>
                                </b-th>
                                <b-th v-else colspan="3" style="border-top:0;">
                                    <b-input-group>
                                        <b-form-input id="polls-table-filter-input" v-model="PollsTableTextFilter"
                                            type="search" :placeholder="filterInputPlaceholderText" @keypress="updateDataTablePage"
                                        ></b-form-input>
                                        <template #append>
                                            <b-button :disabled="!PollsTableTextFilter" @click="PollsTableTextFilter = ''">Clear Filter</b-button>
                                        </template>
                                    </b-input-group>
                                </b-th>
                                <b-th colspan="3" variant="primary" class="text-center">Named Candidate</b-th>
                                <b-th v-show="displayGenericCandidate === true" colspan="3" variant="light" class="text-center">Generic Candidate</b-th>
                                <b-th colspan="3" variant="primary" class="text-center">{{ getPresidentialLabel}}</b-th>
                                <b-th colspan="1" variant="" class="text-center" style="border-top:0; min-width:5em;">
                                    <b-link size="sm" class="px-1" @click="ToggleAllRowDetails" id="expand-all-btn"><small>{{ExpandAllText}}</small></b-link>
                                </b-th>
                            </b-tr>
                        </template>

                        <template v-slot:cell(Date)="data" >
                            <span v-html="data.value"></span>
                        </template>

                        <template v-slot:cell(show_details)="row" >
                            <b-button
                                data-old-click="row.toggleDetails"
                                @click="updatePollsBeingEditedState(row)"
                                variant="outline-secondary"
                                class="btn-data-stats py-0 px-1"
                                v-b-popover.hover.left="'Expand/Collapse Details'"
                            >
                                <i :class="toggleDetailsIconClass(row.detailsShowing)" style="color:#666;"></i>
                            </b-button>
                        </template>

                        <template v-slot:row-details="row">
                            <b-card class="polls-table-details-drawer shadow-sm rounded-0">
                            <b-container fluid class="text-left" style="font-size:smaller;">
                                <b-row>
                                    <b-col>
                                        <b-collapse :id="editPollID(row.item.ID)" class="edit-polls-container">
                                            <edit-poll-form 
                                                :row="row" 
                                                :sources="sources"
                                                :districts="districts"
                                                :pollsterList="pollsterList"
                                                :displayNamedCandidateName="displayNamedCandidateName"
                                                :displayGenericCandidate="displayGenericCandidate" 
                                                :resetNewPollForm="resetNewPollForm"
                                                :alertUserWithToaster="alertUserWithToaster"
                                                :borderClass="borderClass"
                                                :disable-fields="disableFields"
                                                :get-label="getLabel"
                                                :label-map="labelMap"
                                            >
                                                <template v-slot:dToplineCandidateNameLabel><slot name="dToplineCandidateNameLabel">Cand. 1</slot></template>
                                            </edit-poll-form>
                                        </b-collapse>
                                    </b-col>
                                </b-row>
                                <b-row>
                                    <b-col cols="5">
                                        <dl class="row m-0 px-0">
                                            <dt v-if="displayNamedCandidateName" class="col col-3 m-0 px-0 border-bottom">
                                                <slot name="dToplineCandidateNameLabel">Cand. 1</slot>:</dt>  
                                            <dd v-if="displayNamedCandidateName" class="col col-9 m-0 px-0 border-bottom">{{ row.item.D_Topline_Candidate_Name }}</dd>

                                            <dt class="col col-3 m-0 px-0 border-bottom">Source:</dt>  
                                            <dd class="col col-9 m-0 px-0 border-bottom">{{ getSourceText(row.item.Source) }}</dd>

                                            
                                            <dt class="col col-3 m-0 px-0 border-bottom">Pollster:</dt>  
                                            <dd class="col col-9 m-0 px-0 border-bottom">{{ row.item.Pollster }}</dd>

                                            <dt class="col col-3 m-0 px-0 border-bottom">Link:</dt>  
                                            <dd class="col col-9 m-0 px-0 border-bottom">{{ row.item.Link }}</dd>
                                            
                                            <dt class="col col-3 m-0 px-0">Notes:</dt>  
                                            <dd class="col col-9 m-0 px-0">{{ row.item.Notes }}</dd>
                                        </dl>
                                    </b-col>
                                    <b-col cols="5">
                                        <dl class="row m-0 px-0">
                                            <dt  v-if="displayRegion"  class="col col-5 m-0 px-0 border-bottom">Region:</dt>  
                                            <dd  v-if="displayRegion" class="col col-7 m-0 px-0 border-bottom">{{ row.item.Region }}</dd>

                                            <dt class="col col-5 m-0 px-0 border-bottom">Entered by:</dt>  
                                            <dd class="col col-7 m-0 px-0 border-bottom">
                                                {{ row.item.Entered_By }}<br><span v-if="row.item.Created_At !== ''">(<i>{{ formatDateWithTime(row.item.Created_At) }}</i>)</span>
                                            </dd>
                                            
                                            <dt class="col col-5 m-0 px-0">Last updated by:</dt>  
                                            <dd class="col col-7 m-0 px-0">
                                                {{ row.item.Last_Updated_By }}<br><span v-if="row.item.Updated_At !== ''">(<i>{{ formatDateWithTime(row.item.Updated_At) }}</i>)</span>
                                            </dd>
                                        </dl>
                                    </b-col>
                                    <b-col cols="2" class="text-right">
                                    </b-col>
                                </b-row>
                            </b-container>
                            </b-card>
                        </template>
                    </b-table>
                </b-col>
            </b-row>

            <!-- ************************************************************************************** -->
            <!-- *                             LOWER PAGINATION CONTROLS                              * -->
            <!-- ************************************************************************************** -->
            <table-pagination-controls
                :pollsTableTotalPages="pollsTableTotalPages"
                :numberOfPolls="numberOfPolls"
            />
        </b-col>
    </b-row>
</template>

<script setup>
    import { onMounted, computed, ref } from 'vue';
    import TablePaginationControls from '../poll-entry/TablePaginationControls.vue';
    import DataTableCheckboxFilter from '../poll-entry/DataTableCheckboxFilter.vue';
    import EditPollForm from '../poll-entry/EditPollForm.vue';
    import { storeToRefs } from 'pinia';

    // Constants
    import { PollConfig } from '../../pollconfig'

    // Stores
    import { usePollsStore } from '../../stores/polls';

    const pollsStore = usePollsStore();

    // Store refs
    const {
        filteredPolls,
        pollsTableCurrentPage,
        pollsPerPage,
        numberOfPolls,
        sortBy,
        sortDirection,
        filterTableFields,
        pollsTableTextFilter,
        pollsTablePresetFilter,
        pollsTableFilter,
        GetPollPage,
    } = storeToRefs(pollsStore);

    const props = defineProps({
        districts: Array,
        pollsterList: Array,
        sources: Array,
        disableFields: {required: false, default: () => ([]), type: Array},
        enableFuturePolls: {required: false, default: false, type: Boolean},
        displayNamedCandidateName: {required: false, default: true, type: Boolean},
        displayGenericCandidate: {required: false, default: true, type: Boolean},
        displayRegion: {required: false, default: false, type: Boolean},
        alertUserWithToaster: Function,
        borderClass: Function,
        editPollID: Function,
        resetNewPollForm: Function,
        toggleDetailsIconClass: Function,
        UpdateEditPollState: Function,
        labelMap: {required: false,
                    default: () => ({}),
                    type: Object},
    });

    onMounted(() => {
        console.log('PollsDataTable Component mounted.');
    });

    // const pollDataTableFields = ref(["ID", "District", "Date", "D_Topline", "R_Topline", "Indp_Other", "Unsure", "Generic_D", "Generic_R", "Generic_I", "Generic_U", "President_Approval", "President_Disapproval"]);
    const pollDataTableFields = ref([]);

    if (props.displayGenericCandidate === true) {
        pollDataTableFields.value = [
            {key: "District",          sortable: true, label: getLabel("District") },
            {key: "Date",              sortable: true, formatter: 'dateFormatter' },
            {key: "D_Topline",         sortable: true, variant: 'secondary', label: getLabel("D_Topline"), tdClass: 'percentStyle', },
            {key: "R_Topline",         sortable: true, variant: 'secondary', label: getLabel("R_Topline"), tdClass: 'percentStyle', },
            // {key: "Indp_Other",        sortable: true, variant: 'secondary', label: getLabel("Indp_Other"), tdClass: 'percentStyle', },
            {key: "Unsure",            sortable: true, variant: 'secondary', label: getLabel("Unsure"), tdClass: 'percentStyle', },
            {key: "Generic_D",         sortable: true, label: getLabel("Generic_D"), tdClass: 'percentStyle', },
            {key: "Generic_R",         sortable: true, label: getLabel("Generic_R"), tdClass: 'percentStyle', },
            // {key: "Generic_I",         sortable: true, label: getLabel("Generic_I"), tdClass: 'percentStyle', },
            {key: "Generic_U",         sortable: true, label: getLabel("Generic_U"), tdClass: 'percentStyle', },
            {key: "President_Approval",    sortable: true, variant: 'secondary', label: getLabel("President_Approval"), tdClass: 'percentStyle', },
            {key: "President_Disapproval", sortable: true, variant: 'secondary', label: getLabel("President_Disapproval"), tdClass: 'percentStyle',  },
            {key: "President_Unsure",      sortable: true, variant: 'secondary', label: getLabel("President_Unsure"), tdClass: 'percentStyle',  },
            {key: "show_details",      label: "Details" }
        ];
    }
    else {
        pollDataTableFields.value = [
            {key: "District",          sortable: true, label: getLabel("District") },
            {key: "Date",              sortable: true, formatter: 'dateFormatter' },
            {key: "WeeksOut",          sortable: true },
            {key: "D_Topline",         sortable: true, variant: 'secondary', label: getLabel("D_Topline"), tdClass: 'percentStyle', },
            {key: "R_Topline",         sortable: true, variant: 'secondary', label: getLabel("R_Topline"), tdClass: 'percentStyle', },
            // {key: "Indp_Other",        sortable: true, variant: 'secondary', label: getLabel("Indp_Other"), tdClass: 'percentStyle', },
            {key: "Unsure",            sortable: true, variant: 'secondary', label: getLabel("Unsure"), tdClass: 'percentStyle', },
            {key: "President_Approval",    sortable: true, variant: 'secondary', label: getLabel("President_Approval"), tdClass: 'percentStyle', },
            {key: "President_Disapproval", sortable: true, variant: 'secondary', label: getLabel("President_Disapproval"), tdClass: 'percentStyle',  },
            {key: "President_Unsure",      sortable: true, variant: 'secondary', label: getLabel("President_Unsure"), tdClass: 'percentStyle',  },
            {key: "show_details",      label: "Details" }
        ]
    }

    const isAllExpanded = ref(false);
    const filterFuturePolls = ref(['Current', 'Future']);

    // Computed properties
    const configHasPresCandDShortName = computed(() => {
        return PollConfig.configHasPresCandDShortName();
    });

    const configHasPresCandRShortName = computed(() => {
        return PollConfig.configHasPresCandRShortName();
    });

    const configGetPresCandDShortName = computed(() => {
        return PollConfig.presCandDShortName;
    });

    const configGetPresCandRShortName = computed(() => {
        return PollConfig.presCandRShortName;
    });

    const getPresidentialLabel = computed(() => {
        return PollConfig.getPresidentialLabel();
    });

    const PollsTableFilter = computed({
        get() {
            return pollsTableFilter.value;
        },
        set(val) {
            pollsTableFilter.value = val;
        }
    });

    const PollsTableTextFilter = computed({
        get() {
            return pollsTableTextFilter.value;
        },
        set(val) {
            pollsTableTextFilter.value = val;
            pollsTableFilter.value = pollsTablePresetFilter.value + pollsTableTextFilter.value;
        }
    });

    const FilterTableFields = computed({
        get() {
            return filterTableFields.value;
        },
        set(val) {
            filterTableFields.value = val;
        }
    });

    const pollsTableTotalPages = computed(() => {
        return Math.ceil(numberOfPolls.value / pollsPerPage.value);
    });

    const ExpandAllText = computed(() => {
        return isAllExpanded.value ? 'Collapse All' : 'Expand  All';
    });

    const filterInputPlaceholderText = computed(() => {
        let text = "Filter by ";
        for (var i = 0; i < filterTableFields.value.length; i++) {
            text += getLabel(filterTableFields.value[i]) + (i == filterTableFields.value.length-2 ? " or " : (i == filterTableFields.value.length-1 ? ". ": ", ") );
        }
        return text;
    });

    // Component methods
    const pollsFilter = async () => {
    // async function pollsFilter(ctx, callback) {
        let callback = null;
        await pollsStore.GetPollPage(callback);


        if (props.displayRegion) {
            let polls = filteredPolls.value.filter(function(item) {
                let polldate        = new Date(item['Date']);
                let now             = new Date();

                if (filterFuturePolls.value.includes('Current')) {
                    if (polldate <= now) {
                        return true;
                    }
                }

                if (filterFuturePolls.value.includes('Future')) {
                    if (polldate > now) {
                        return true;
                    }
                }

                return false;
            });
            // console.log(polls);
            return polls;

        }

        return filteredPolls.value
    }

    function formatDateWithTime(dateString) {
        const date = new Date(dateString);
        return date.toLocaleString('en-US', {
            // timeZone: 'America/New_York',
            // timeZone: 'Europe/Berlin',
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        });
    }

    function formatDate(dateString) {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            // Show the date entered as UTC
            timeZone: 'UTC',
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
    }

    function dateFormatter(value) {
        let polldate        = new Date(value);
        let now             = new Date();

        if (now >= polldate) {
            return formatDate(polldate);
            // return formatDate(polldate) + '<div>' + polldate + '</div>';
        }

        return '<i class="far fa-calendar-alt"></i> ' + formatDate(polldate);
    }

    function getLabel(key) {
        if (key === "D_Topline") {
            return props.labelMap["D_Topline"] ? props.labelMap["D_Topline"] : "D" 
        }
        else if (key === "R_Topline") {
            return props.labelMap["R_Topline"] ? props.labelMap["R_Topline"] : "R" 
        }
        else if (key === "Indp_Other") {
            return props.labelMap["Indp_Other"] ? props.labelMap["Indp_Other"] : "I/O" 
        }
        else if (key === "Unsure") {
            return props.labelMap["Unsure"] ? props.labelMap["Unsure"] : "U" 
        }
        else if (key === "Generic_D") {
            return props.labelMap["Generic_D"] ? props.labelMap["Generic_D"] : "D" 
        }
        else if (key === "Generic_R") {
            return props.labelMap["Generic_R"] ? props.labelMap["Generic_R"] : "R" 
        }
        else if (key === "Generic_I") {
            return props.labelMap["Generic_I"] ? props.labelMap["Generic_I"] : "I/O" 
        }
        else if (key === "Generic_U") {
            return props.labelMap["Generic_U"] ? props.labelMap["Generic_U"] : "U" 
        }
        else if (key === "President_Approval") {
            return props.labelMap["President_Approval"] ? props.labelMap["President_Approval"] : "Approval" 
        }
        else if (key === "President_Disapproval") {
            return props.labelMap["President_Disapproval"] ? props.labelMap["President_Disapproval"] : "Disapprove" 
        }
        else if (key === "President_Unsure") {
            return props.labelMap["President_Unsure "] ? props.labelMap["President_Unsure"] : "U"
        }

        return props.labelMap[key] ? props.labelMap[key] : key;
    }

    function getSourceText(source) {
        return PollConfig.getSourceText(source);
    }

    function updatePollsBeingEditedState(row) {
        // console.log('+++ updatePollsBeingEditedState( ' + row.item.ID + ' ) ++++');
        row.toggleDetails();
    }

    function ToggleAllRowDetails() {
        // console.log("ToggleAllRowDetails");
        isAllExpanded.value = !isAllExpanded.value;
        filteredPolls.value.forEach((el,i) => { el._showDetails = isAllExpanded.value; });
        if( !isAllExpanded.value ) {
        }
    }

    function updateDataTablePage () {
        // console.log('** updateDataTablePage() pollsTableFilter = ' + pollsTableFilter.value + ' **');
        if( pollsTableFilter.value !== null && pollsTableFilter.value !== "") {
            pollsTableCurrentPage.value = 1;
        }
    }

    function rowClass(item, type) {
        if (!item || type !== 'row')
            return;
        // console.log(item);
        let polldate        = new Date(item['Date']);
        let now             = new Date();
        if (polldate <= now) {
            if (item['D_Topline'] === '-' && 
                item['R_Topline'] === '-' &&
                item['Indp_Other'] === '-' &&  
                item['Unsure'] === '-' &&
                item['Generic_D'] === '-' &&
                item['Generic_R'] === '-' &&
                item['Generic_I'] === '-' &&
                item['Generic_U'] === '-' &&
                item['President_Approval'] === '-' &&  
                item['President_Disapproval'] === '-' &&
                item['President_Unsure'] === '-'
            ) {
                return 'table-danger';
            }
            else {
                return;
            }
        }

        return;
    }

    function isRowExpanded(item) {
        return item.detailsExpanded;
    }
    

</script>

<style scoped>
.table-footer-style {
    font-style: italic;
    font-size: small;
}
</style>
<style>
#polls-table thead tr {
    font-size: large;
    text-align: center;
}
#polls-table tbody tr:hover  td.table-secondary {
    background-color: #cccdce !important;
}
#polls-table tbody tr:hover  td.table-gray-400 {
    background-color: #d5d6d7 !important;
}
#polls-table tbody tr.table-danger:hover  td.table-secondary {
    background-color: #faa0a0 !important;
}
#polls-table tbody tr.table-danger:hover  td.table-gray-400 {
    background-color: #fbadad !important;
}

#polls-table tbody tr td {
    font-size: larger;
    text-align: center;
}
.columnStyle,
.percentStarStyle,
.percentStyle {
    font-family: SFMono-Regular,Menlo,Monaco,Consolas,"Liberation Mono","Courier New",monospace!important;
    text-align:right !important;
}
.percentStyle:after {
    content: "%";
}
#polls-table tbody tr td:last-child {
    text-align: center !important;
}
#polls-table tbody tr td:last-child:after {
    content: "";
}
#polls-table .polls-table-details-drawer {
    margin-top: -12px;
    border-top-left-radius: 0;
    border-top: 0;
    border-top-right-radius: 0;
    background-color: #e7e8e9;
}
#polls-table tr.b-table-details td {
    padding-bottom: 0px;
}
</style>
